import { TextContent } from './TextContent';
import clsx from 'clsx';
import { forwardRef, cloneElement, createElement, type ForwardedRef } from 'react';
import { mergeProps } from 'react-aria';
import { Text as AriaText } from 'react-aria-components';

import { type PolymorphicProps, type TypographyProps } from './types';
import { filterTypographyProps, getTypographyStyles } from './utils';

export type Props = PolymorphicProps<TypographyProps>;

export const Text = forwardRef(function _Text(props: Props, ref: ForwardedRef<HTMLSpanElement>) {
  const { typographyProps, restProps } = filterTypographyProps(props);
  const { truncate, ...restTypographyProps } = typographyProps;
  const styles = getTypographyStyles(restTypographyProps);

  if (restProps.asChild) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, asChild, ...rest } = restProps;
    const { children: grandchildren } = children.props;
    const content = <TextContent truncate={truncate}>{grandchildren}</TextContent>;
    /*
     * **The ref is passed only when there is one**, and that guard is load-bearing (PLT-6674).
     *
     * `mergeProps` merges two refs only when both are truthy (`key === 'ref' && a && b`);
     * otherwise the second wins. Nothing usually passes a ref to `Text`, so `ref` is `null` here,
     * and React's `cloneElement` treats `ref: null` as a ref worth honouring — `config.ref !==
     * undefined` — so the child's own ref is replaced by nothing. Any `asChild` child that was
     * handed a ref by an outer cloner loses it silently: no error, and the only symptom is
     * whatever needed the DOM node failing quietly. It cost a tooltip that opened but never
     * positioned itself, because React Aria's `Focusable` had supplied exactly such a ref.
     *
     * Spreading conditionally keeps the key absent rather than `null`, which is what
     * `cloneElement` reads as "leave the child's ref alone".
     */
    return cloneElement(
      children,
      mergeProps(rest, { ...(ref === null ? {} : { ref }), className: clsx(children.props.className, styles) }),
      content,
    );
  }

  const { children, ...rest } = restProps;
  const content = <TextContent truncate={truncate}>{children}</TextContent>;

  return createElement(AriaText, mergeProps(rest, { ref, className: styles }), content);
});
